import _ from "lodash";
import {
  Component,
} from "react";
import {
  Link,
} from "react-router-dom";
import {
  renderChildren,
  withLocation,
} from "CommonCore/router-util";
import {
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
} from "react-bootstrap";
import {
  createSelector,
} from "reselect";
import cls from "classnames";

import uiconfig, {
  allows,
  ifAllowed,
  ifValidEntity,
} from "CommonState/uiconfig";
import store from "CommonState/store";
import {
  findPathComponent,
  getPathComponent,
  getPathPrefix,
  popAndReplaceComponent,
  popComponent,
  pushComponent,
} from "CommonCore/location-util";
import {
  createInjectsSelector,
  defaultFromSchema,
  injectFunction,
  injectsFromSchema,
  injectsFromSchemaAndRenderMethods,
} from "CommonCore/schema-util";
import Breadcrumb from "CommonComponents/breadcrumb";
import EntityTable from "CommonComponents/entity-table";
import {
  formatValue,
  Spinner,
} from "CommonComponents/bricks";
import Confirmation from "CommonComponents/confirmation";
import {
  DetailEditor,
  DetailView,
} from "CommonComponents/detail-view";

function getBreadcrumbs(api) {
  return {
    breadcrumbs: _.assign({
      list: api.names.name,
      detail: api.id,
    }, api.breadcrumbs),
    breadcrumbInjects: api.breadcrumbs && api.breadcrumbs.detail && api.breadcrumbs.detail.inject || [],
  };
}

function decorateIf(decorator, condition) {
  return Component => {
    return condition ? decorator(Component) : Component;
  };
}

export function createListPageConnector({
  api, endpointArgs, view, renderMethods, fieldToDetailLink,
  perRowActions, extraActions, toolbarComponents, noActions,
  multiItemActions, noCache, filterProps,
}) {
  let getRenderMethods = () => renderMethods;

  if (fieldToDetailLink) {
    const linkSchema = _.find(api.schema.table, s => s.field === fieldToDetailLink);
    getRenderMethods = createSelector(_.identity, baseUrl => _.assign({}, renderMethods, {
      [fieldToDetailLink]: (field, entity) => entity &&
          <Link to={`${ baseUrl }/${ api.id(entity) }/details`}>
            {formatValue(entity, linkSchema)}
          </Link>,
    }));
  }

  view = view || api;

  const { breadcrumbs } = getBreadcrumbs(api);

  const injects = injectsFromSchemaAndRenderMethods(view.schema, renderMethods);
  const selectInjects = createInjectsSelector(injects);

  const actionsGenerator = (uiconfig, entity, onDelete, baseUrl) => {
    const actions = [
      {
        display: "Anzeigen",
        icon: "eye",
        execute: e => `${ baseUrl }/${ api.id(e) }/details`,
      },
      { divider: true },
      ((uiconfig && uiconfig[`edit.${ api.names.name }`])
        || (perRowActions && allows(entity, "darfBearbeiten", true))) && {
        display: "Bearbeiten",
        icon: "pencil-alt",
        execute: e => `${ baseUrl }/${ api.id(e) }/details/edit`,
      },
      ((uiconfig && uiconfig[`delete.${ api.names.name }`])
        || (perRowActions && allows(entity, "darfEntfernen", true))) && {
        display: "Löschen",
        icon: "times",
        execute: onDelete,
      },
    ];
    extraActions && extraActions(entity, actions);
    return actions;
  };

  const selectActions = createSelector(
    self => self.props.uiconfig,
    (self, entity) => perRowActions && entity,
    self => self.onDelete,
    self => self.baseUrl,
    actionsGenerator,
  );

  const checkInflight = props => _.some(_.pick(props, view.allInflightProperties));

  const uiListDecorator = endpointArgs ? api.uiList(endpointArgs) : view.uiList;

  @uiconfig
  @ifAllowed(`page.${ api.names.name }`)
  @store(...view.allInflightProperties, ...injects)
  @uiListDecorator
  @decorateIf(api.noCache, noCache)
  class TableConnector extends Component {
    constructor() {
      super();
      this.state = {};
    }

    render() {
      const {
        ifAllowed,
      } = this.props;
      const {
        toDelete,
        deleteFailedMessage,
      } = this.state;
      const baseUrl = this.baseUrl;
      return <div>
        <Breadcrumb path={baseUrl} name={breadcrumbs.list} />
        <EntityTable
          list={view.names.listName}
          data={this.props[view.names.uiListName]}
          addNewPath={ifAllowed(`new.${ api.names.name }`, `${ baseUrl }/neu`)}
          schema={view.schema}
          inflight={checkInflight(this.props)}
          renderMethods={getRenderMethods(baseUrl)}
          injects={selectInjects(this.props)}
          actions={!noActions && this.getActions}
          toolbarComponents={toolbarComponents}
          multiItemActions={multiItemActions}
          filterProps={filterProps}
        />
        {toDelete && <Confirmation
          header={`${ api.schema.displayName } löschen`}
          body={<span>Sind Sie sicher, dass Sie <strong>{api.schema.formatWithDefinitiveArticle(toDelete)}</strong> löschen
            möchten? Diese Aktion kann <strong>nicht</strong> rückgängig gemacht werden.</span>}
          action='Löschen'
          errorMessage={deleteFailedMessage}
          inflight={this.props[api.names.currentInflightName]}
          onCancel={this.onDeleteCancel}
          onConfirm={this.onDeleteConfirm}
        />}
      </div>;
    }

    get baseUrl() {
      return getPathPrefix(location, findPathComponent(location, api.names.name));
    }

    getActions = entity => selectActions(this, entity);

    onDelete = entity => {
      this.setState({
        toDelete: entity,
        deleteFailedMessage: null,
      });
    };

    onDeleteCancel = () => {
      this.setState({ toDelete: null });
    };

    onDeleteConfirm = async function() {
      try {
        await this.props[api.names.deleteActionName](api.id(this.state.toDelete));
        this.setState({ toDelete: null });
      } catch(err) {
        this.setState({
          deleteFailedMessage:
            (err.response && (err.response.nachricht || err.response.um))
            || "Es ist ein Fehler aufgetreten.",
        });
      }
    }.bind(this);
  }

  return TableConnector;
}

export function createDetailTabViewConnector({ api, extra }) {
  const injects = injectsFromSchema(api.schema);
  const { breadcrumbs, breadcrumbInjects } = getBreadcrumbs(api);

  @withLocation
  @store(...injects, ...breadcrumbInjects)
  @api.current
  @ifAllowed(`page.${ api.names.name }`)
  @ifValidEntity(api.names.currentName)
  class DetailTabViewConnector extends Component {
    render() {
      const {
        location,
      } = this.props;
      const entity = this.props[api.names.currentName] || {};
      const tab = getPathComponent(location, 3);

      const breadcrumbData = [
        { name: breadcrumbs.list, path: `/${ api.names.name }` },
        { name: injectFunction(breadcrumbs.detail, this.props)(entity), path: `/${ api.names.name }/${ api.id(entity) }` },
      ];

      return <div>
        <Breadcrumb data={breadcrumbData} />
        <Container fluid style={{ marginRight: 15 }}>
          <Row>
            <Nav variant='tabs' activeKey={tab} onSelect={this.navigate} id='ue-tabs'>
              <NavItem><NavLink eventKey='details'>Details</NavLink></NavItem>
              {extra && extra.map(({ path, header }) => <NavItem key={path}><NavLink eventKey={path}>{header}</NavLink></NavItem>)}
            </Nav>
          </Row>
          <Row className='tab'>
            {renderChildren(this.props)}
          </Row>
        </Container>
      </div>;
    }

    navigate = eventKey => {
      const { history, location } = this.props;
      history.push(popAndReplaceComponent(location, 3, eventKey));
    };
  }

  return DetailTabViewConnector;
}

export function createDetailViewConnector({ api, renderMethods, bordered, noCache, toolBar, toolBarEmbedded }) {
  const injects = injectsFromSchemaAndRenderMethods(api.schema, renderMethods);
  const selectInjects = createInjectsSelector(injects);
  const checkInflight = props => _.some(_.pick(props, api.allInflightProperties));
  const { breadcrumbs, breadcrumbInjects } = getBreadcrumbs(api);

  @uiconfig
  @store(...injects, ...breadcrumbInjects)
  @api.current
  @decorateIf(api.noCache, noCache)
  @ifAllowed(`page.${ api.names.name }`)
  @ifValidEntity(api.names.currentName)
  class DetailViewConnector extends Component {
    render() {
      const {
        location,
        ifAllowed,
        ifEntityAllows,
      } = this.props;
      const entity = this.props[api.names.currentName];
      const injects = selectInjects(this.props);
      const toEdit = (ifAllowed(`edit.${ api.names.name }`, true)
        || (api.perEntityAccess && ifEntityAllows(entity, "darfBearbeiten", true)))
        && pushComponent(location, "edit");

      const ToolBarComponent = toolBar;

      const baseUrl = getPathPrefix(location, findPathComponent(location, api.names.name));

      return <div className={cls({ "detail-form": true, bordered })}>
        {bordered && <Breadcrumb data={[
          { path: baseUrl, name: breadcrumbs.list },
          { name: entity && injectFunction(breadcrumbs.detail, this.props)(entity) },
        ]} />}
        {ToolBarComponent && <ToolBarComponent entity={entity} />}
        {checkInflight(this.props)
          ? <Spinner />
          :
          <DetailView
            entity={entity}
            schema={api.schema}
            injects={injects}
            toEdit={toEdit}
            renderMethods={renderMethods}
            embeddedToolBar={toolBarEmbedded}
          />
        }
      </div>;
    }
  }

  return DetailViewConnector;
}

export function createDetailEditorConnector({ api, bordered, noCache, locationAfterSave, schema: schemaOverride, beforeSave, customControls, prepareEntity }) {
  const schema = schemaOverride || api.schema;
  const injects = injectsFromSchema(schema);
  const selectInjects = createInjectsSelector(injects);
  const checkInflight = props => _.some(_.pick(props, api.allInflightProperties));
  const { breadcrumbs, breadcrumbInjects } = getBreadcrumbs(api);

  @store(...injects, ...breadcrumbInjects)
  @api.current
  @ifValidEntity(api.names.currentName)
  @decorateIf(api.noCache, noCache)
  @ifAllowed(`edit.${ api.names.name }`, props => !props.params[api.names.currentName])
  @ifAllowed(`new.${ api.names.name }`, props => !!props.params[api.names.currentName])
  class DetailEditorConnector extends Component {
    constructor() {
      super();
      this.state = {
        edits: {},
        entity: {},
      };
    }

    componentDidUpdate(prevProps) {
      const currentEntity = this.props[api.names.currentName];
      const prevEntity = prevProps[api.names.currentName];

      // Nur updaten wenn sich die relevante Prop geändert hat
      if (currentEntity !== prevEntity) {
        if (currentEntity) {
          this.setState({
            entity: prepareEntity ? prepareEntity(currentEntity) : currentEntity,
          });
        } else {
          this.setState({
            entity: schema.makeNew ? schema.makeNew(this.props) : currentEntity,
          });
        }
      }
    }

    get entity() {
      const entity = this.state.entity;
      const entityWithEdits = _.assign({}, entity, this.state.edits);
      return _.assign(defaultFromSchema(schema, selectInjects(this.props), entityWithEdits), entityWithEdits);
    }

    render() {
      const {
        location,
      } = this.props;
      const inflight = this.props[api.names.currentInflightName];
      const originalEntity = this.props[api.names.currentName];
      const entity = this.entity;
      const { validationMessage, validationErrors } = this.state;
      const injects = selectInjects(this.props);
      const baseUrl = this.baseUrl;

      return <Container className={cls({ "detail-form": true, bordered: bordered || !api.id(entity) })}>
        {!api.id(entity) && <Breadcrumb data={[
          { path: baseUrl, name: breadcrumbs.list },
          { name: `${ schema.displayName } hinzufügen` },
        ]} />}
        {api.id(entity) && bordered && <Breadcrumb data={[
          { path: baseUrl, name: breadcrumbs.list },
          { name: originalEntity && injectFunction(breadcrumbs.detail, this.props)(originalEntity) },
        ]} />}
        {checkInflight(this.props)
          ? <Spinner />
          : <DetailEditor
            entity={entity}
            onChange={this.update}
            parentUrl={popComponent(location)}
            schema={schema}
            inflight={inflight}
            injects={injects}
            customControls={customControls}
            validationMessage={validationMessage}
            validationErrors={validationErrors}
            onClose={this.onClose}
            onSave={this.save}
          />
        }
      </Container>;
    }

    get baseUrl() {
      const { location } = this.props;
      return getPathPrefix(location, findPathComponent(location, api.names.name));
    }

    update = changes => {
      const edits = _.assign({}, this.state.edits, changes);
      const { validateEdits } = schema;
      validateEdits && validateEdits(this.props[api.names.currentName], edits, this.state.edits);
      this.setState({ edits });
    };

    onClose = () => {
      this.close();
    };

    close = id => {
      const { history, location } = this.props;
      history.push(
        locationAfterSave
? locationAfterSave(id, this.entity)
          : id
? `${ this.baseUrl }/${ id }`
            : popComponent(location),
      );
    };

    save = async function() {
      const save = this.props[api.names.saveActionName];
      let result;

      try {
        let beforeSaveChangedEntity = null;
        if (beforeSave) {
          // Validierung und Objektänderungen im Editpanel
          beforeSaveChangedEntity = await beforeSave(this.entity, this.props);
        }
        result = await save(beforeSaveChangedEntity ? beforeSaveChangedEntity : this.entity);
      } catch(e) {
        const resp = e.response || {};
        this.setState({
          validationMessage: resp.nachricht || resp.um || "Es ist ein Fehler aufgetreten.",
          validationErrors: resp.details,
        });
        return;
      }
      this.close(api.id(result));
    }.bind(this);
  }

  return DetailEditorConnector;
}
